import { Box, Typography, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  LuCircleCheckBig,
  LuCloud,
  LuFactory,
  LuHash,
  LuCalendar,
  LuMapPin,
  LuLayers,
  LuLeaf,
  LuQrCode,
  LuRecycle,
  LuShieldCheck,
  LuWrench,
} from "react-icons/lu";
import MetricCard from "../../../app_components/MetricCard";
import config from "../../../config";
import { getOrgTokens } from "../../../theme/style/organizationPalette";
import { dedupeMaterials, withMaterialCompositionPercentage } from "./dedupeMaterials";

const GREEN = "#659d33";
const GREEN_DARK = "#3f6d1d";

const useSummaryTok = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tok = getOrgTokens(isDark);
  return { t, tok, isDark };
};

function Panel({ title, Icon, children }) {
  const { tok } = useSummaryTok();
  return (
    <Box sx={{ mt: 2, borderRadius: "18px", border: `1px solid ${tok.border.card}`, backgroundColor: tok.bg.card, p: 2 }}>
      <Box sx={{ mb: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
        <Box sx={{ width: 28, height: 28, borderRadius: "10px", display: "grid", placeItems: "center", backgroundColor: "rgba(101,157,51,0.12)", color: GREEN }}>
          <Icon size={15} />
        </Box>
        <Typography sx={{ fontSize: "14px", fontWeight: 600, color: tok.text.primary, fontFamily: "Poppins", textAlign: "left" }}>{title}</Typography>
      </Box>
      {children}
    </Box>
  );
}

function Row({ label, value }) {
  const { tok } = useSummaryTok();
  if (!value && value !== 0) return null;
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 1.5, py: 0.75, borderBottom: `1px dashed ${tok.border.card}`, "&:last-child": { borderBottom: "none" } }}>
      <Typography sx={{ fontSize: "12px", color: tok.text.muted, fontFamily: "Poppins", textAlign: "left" }}>{label}</Typography>
      <Typography sx={{ fontSize: "12px", fontWeight: 500, color: tok.text.primary, fontFamily: "Poppins", textAlign: "right" }}>{value}</Typography>
    </Box>
  );
}

function MaterialBar({ name, pct }) {
  const { tok } = useSummaryTok();
  const safePct = Math.max(0, Math.min(100, Number(pct) || 0));
  return (
    <Box sx={{ py: 0.75 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Typography sx={{ fontSize: "12px", color: tok.text.primary, fontFamily: "Poppins", textAlign: "left" }}>{name}</Typography>
        <Typography sx={{ fontSize: "12px", fontWeight: 600, color: tok.text.primary, fontFamily: "Poppins" }}>{safePct}%</Typography>
      </Box>
      <Box sx={{ mt: 0.5, height: 6, borderRadius: "999px", backgroundColor: tok.border.card, overflow: "hidden" }}>
        <Box sx={{ height: "100%", width: `${safePct}%`, backgroundColor: GREEN, borderRadius: "999px" }} />
      </Box>
    </Box>
  );
}

function BadgeRow({ items }) {
  if (!items.length) return null;
  return (
    <Box sx={{ mb: 1, display: "flex", flexWrap: "wrap", gap: 0.75 }}>
      {items.map((i) => (
        <Box key={i} component="span" sx={{ borderRadius: "999px", backgroundColor: "rgba(101,157,51,0.10)", border: `1px solid ${GREEN}33`, px: 1.25, py: 0.5, fontSize: "10px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: GREEN, fontFamily: "Poppins" }}>
          {i}
        </Box>
      ))}
    </Box>
  );
}

/**
 * Mobile-first public summary shown when a DPP's QR code is scanned.
 * Structural layout (hero + mini-stats, quick facts grid, section panels,
 * data-carrier footer) follows the reference mock at d:\ArivuCodebase\Dpp.jsx,
 * but every value is pulled from the real dpp_format fields, and all
 * styling uses this app's own design tokens (getOrgTokens, ARIVU green,
 * Poppins) rather than the mock's own Tailwind/emerald treatment.
 */
const DppPublicSummary = ({ dpp, entityId }) => {
  const { t, tok } = useSummaryTok();
  const [photos, setPhotos] = useState([]);

  useEffect(() => {
    if (!entityId) { setPhotos([]); return; }
    fetch(`${config.apiUrl}/api/fetch_emission_reference_files?ID=${entityId}`)
      .then((res) => res.json())
      .then((data) => setPhotos(data.filter((f) => f.scope === "DPP Evidence" && f.is_photo && !f.hidden_in_photo_section)))
      .catch(() => {});
  }, [entityId]);

  if (!dpp) return null;

  // dpp_format/dppPassportSections are shared verbatim across Product/Project/
  // Building, so labels are hardcoded to say "Product" — swap to "Project"/
  // "Building" when the scanned passport's entity_type says so. Word-boundary
  // regex (not a plain substring replace) so "Production"/"Production
  // Facility" aren't corrupted into "Projection"/"Projection Facility".
  const renameLabel = (str) => {
    if (typeof str !== "string") return str;
    const to = { project: "Project", building: "Building" }[dpp.entity_type];
    if (!to) return str;
    return str
      .replace(/\bProducts\b/g, `${to}s`)
      .replace(/\bProduct\b/g, to)
      .replace(/\bproducts\b/g, `${to.toLowerCase()}s`)
      .replace(/\bproduct\b/g, to.toLowerCase());
  };

  // Identity fields are shared/generic in dpp_format ("product_name"/
  // "product_id") for Product, but Project/Building passports store their
  // real name/id under project_name/project_id or building_name/building_id
  // (see getDppFormat in ProductDPP_Format.js).
  const nameField = { project: "project_name", building: "building_name" }[dpp.entity_type] || "product_name";
  const idField = { project: "project_id", building: "building_id" }[dpp.entity_type] || "product_id";

  const materials = withMaterialCompositionPercentage(dedupeMaterials(Array.isArray(dpp.materials) ? dpp.materials.filter((m) => m.description || m.type) : []));
  const certifications = Array.isArray(dpp.certifications) ? dpp.certifications.filter((c) => c.name) : [];
  const complianceBadges = [
    dpp.compliance_ce && "CE",
    dpp.compliance_rohs && "RoHS",
    dpp.compliance_reach && "REACH",
    dpp.compliance_weee && "WEEE",
  ].filter(Boolean);
  const careNotes = [dpp.repair_maintenance_notes, dpp.installation_end_of_life_notes, dpp.design_for_recycling_notes].filter(Boolean);
  const dppId = dpp.eu_dpp_registry_id || dpp[idField];

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: tok.bg.page || "#f6f7f5" }}>
      <Box sx={{ maxWidth: 440, mx: "auto", px: 2.5, py: 3 }}>
        {/* Brand bar */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{ width: 30, height: 30, borderRadius: "8px", display: "grid", placeItems: "center", backgroundColor: GREEN, color: "#fff" }}>
            <LuLeaf size={15} />
          </Box>
          <Box sx={{ lineHeight: 1.1 }}>
            <Typography sx={{ fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.16em", color: GREEN, fontFamily: "Poppins" }}>ARIVU</Typography>
            <Typography sx={{ fontSize: "10px", color: tok.text.muted, fontFamily: "Poppins" }}>{t("Circularity Intelligence")}</Typography>
          </Box>
        </Box>

        {/* Hero */}
        <Box sx={{ mt: 2.5, borderRadius: "24px", p: 3, background: `linear-gradient(135deg, ${GREEN} 0%, ${GREEN_DARK} 100%)`, boxShadow: "0 20px 45px -20px rgba(63,109,29,0.5)" }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
            <Box component="span" sx={{ borderRadius: "999px", backgroundColor: "rgba(255,255,255,0.12)", px: 1.25, py: 0.5, fontSize: "9px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "rgba(255,255,255,0.85)" }}>
              {t(renameLabel("Digital Product Passport"))}
            </Box>
            {dpp._id && (
              <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, borderRadius: "999px", backgroundColor: "rgba(255,255,255,0.12)", px: 1.25, py: 0.5, fontSize: "9px", fontWeight: 700, color: "rgba(255,255,255,0.85)" }}>
                <LuCircleCheckBig size={11} /> {t("Published")}
              </Box>
            )}
          </Box>
          <Typography sx={{ mt: 2, fontSize: "19px", fontWeight: 700, lineHeight: 1.3, color: "#fff", fontFamily: "Poppins", textAlign: "left", wordBreak: "break-word", overflowWrap: "anywhere" }}>
            {dpp[nameField] || t(renameLabel("Untitled product"))}
          </Typography>
        </Box>

        {/* Key metrics */}
        <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1.25 }}>
          <MetricCard title={t("GWP")} value={dpp.gwp_total || 0} unit="kgCO₂e" icon={LuCloud} accent="#2563eb" bg="rgba(37, 99, 235, 0.1)" variant="stage" />
          <MetricCard title={t("Recyclability")} value={dpp.recyclability_percent || 0} unit="%" icon={LuRecycle} accent={GREEN} bg="rgba(101,157,51,0.1)" variant="stage" />
          <MetricCard title={t("Repair Score")} value={dpp.repairability_score || 0} unit="/10" icon={LuWrench} accent="#d97706" bg="rgba(217,119,6,0.1)" variant="stage" />
        </Box>

        {/* Photos */}
        {photos.length > 0 && (
          <Box sx={{ mt: 2, display: "flex", gap: 1, overflowX: "auto", pb: 0.5 }}>
            {photos.map((p) => (
              <Box key={p.id} sx={{ width: 96, height: 96, borderRadius: "14px", overflow: "hidden", border: `1px solid ${tok.border.card}`, flexShrink: 0 }}>
                <img src={p.url} alt={p.name} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
              </Box>
            ))}
          </Box>
        )}

        {/* Quick facts */}
        <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1.25 }}>
          <MetricCard title={t("GTIN")} value={dpp.gtin || "-"} icon={LuHash} accent={GREEN} bg="rgba(101,157,51,0.1)" variant="stage" />
          <MetricCard title={t("Manufactured")} value={dpp.production_date || "-"} icon={LuCalendar} accent={GREEN} bg="rgba(101,157,51,0.1)" variant="stage" />
          <MetricCard title={t("Origin")} value={dpp.country_of_origin || "-"} icon={LuMapPin} accent={GREEN} bg="rgba(101,157,51,0.1)" variant="stage" />
          <MetricCard title={t("Batch")} value={dpp.batch_number || "-"} icon={LuLayers} accent={GREEN} bg="rgba(101,157,51,0.1)" variant="stage" />
        </Box>

        {/* Sustainability */}
        <Panel title={t("Sustainability")} Icon={LuLeaf}>
          <Row label={t("Global Warming Potential")} value={dpp.gwp_total ? `${dpp.gwp_total} kgCO₂e` : null} />
          <Row label={t("Energy Consumption")} value={dpp.energy_consumption_value ? `${dpp.energy_consumption_value} kWh` : null} />
          <Row label={t("Reference Service Life")} value={dpp.service_life ? `${dpp.service_life} yrs` : null} />
          <Row label={t("Expected Lifespan")} value={dpp.expected_lifespan_years ? `${dpp.expected_lifespan_years} yrs` : null} />
        </Panel>

        {/* Materials */}
        {materials.length > 0 && (
          <Panel title={t("Material Composition")} Icon={LuLayers}>
            {materials.map((m, i) => (
              <MaterialBar key={i} name={m.description || m.type} pct={m.material_composition_percentage} />
            ))}
          </Panel>
        )}

        {/* Circularity & Repair */}
        <Panel title={t("Circularity & Repair")} Icon={LuRecycle}>
          <Row label={t("Circularity Index (MCI)")} value={dpp.mci ? `${dpp.mci}%` : null} />
          <Row label={t("Recyclability")} value={dpp.recyclability_percent ? `${dpp.recyclability_percent}%` : null} />
          <Row label={t("Repairability Rating")} value={dpp.repairability_rating} />
          <Row label={t("Durability Rating")} value={dpp.durability_rating} />
          <Row label={t("Take-back Program")} value={dpp.take_back_available ? t("Available") : null} />
        </Panel>

        {/* Compliance */}
        <Panel title={t("Compliance")} Icon={LuShieldCheck}>
          <BadgeRow items={[...complianceBadges, ...certifications.map((c) => c.name)]} />
          <Row label={t("EcoDesign")} value={dpp.compliance_ecodesign} />
        </Panel>

        {/* Producer */}
        <Panel title={t("Producer")} Icon={LuFactory}>
          <Row label={t("Manufacturer")} value={dpp.facility_manufacturer} />
          <Row label={t("Production Facility")} value={dpp.production_facility} />
          <Row label={t("Unique Operator ID")} value={dpp.unique_operator_id} />
          <Row label={t("EU Operator")} value={dpp.eu_operator} />
          <Row label={t("EORI")} value={dpp.eori} />
        </Panel>

        {/* Care */}
        {careNotes.length > 0 && (
          <Panel title={t("Care & End-of-Life")} Icon={LuWrench}>
            {careNotes.map((note, i) => (
              <Typography key={i} sx={{ fontSize: "12px", lineHeight: 1.6, color: tok.text.muted, fontFamily: "Poppins", textAlign: "left", mb: i < careNotes.length - 1 ? 1 : 0 }}>
                {note}
              </Typography>
            ))}
          </Panel>
        )}

        {/* Data carrier */}
        {dppId && (
          <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 1.5, borderRadius: "16px", border: `1px solid ${tok.border.card}`, backgroundColor: tok.bg.card, p: 2 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: "12px", display: "grid", placeItems: "center", backgroundColor: GREEN, color: "#fff", flexShrink: 0 }}>
              <LuQrCode size={18} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: "10px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", color: tok.text.muted, fontFamily: "Poppins", textAlign: "left" }}>
                {t("DPP ID")}
              </Typography>
              <Typography sx={{ fontSize: "12px", fontWeight: 500, color: tok.text.primary, fontFamily: "Poppins", textAlign: "left", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {dppId}
              </Typography>
            </Box>
          </Box>
        )}

        <Typography sx={{ mt: 3, fontSize: "10px", lineHeight: 1.6, color: tok.text.muted, fontFamily: "Poppins", textAlign: "center" }}>
          {t("Powered by")} <Box component="span" sx={{ fontWeight: 700, color: GREEN }}>ARIVU</Box>
        </Typography>
      </Box>
    </Box>
  );
};
export default DppPublicSummary;
